import React, { useState } from 'react';
import { Row, Col, Form, Button } from 'react-bootstrap';
import { useQueryClient } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileCsv } from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';

import { useForm } from '../../hooks/useForm';
import {
  useFetchUsers,
  useDeleteUser,
  useExportUsers,
} from '../../services/users';
import PrivateLayout from '../layouts/PrivateLayout';
import Panel from '../common/Panel';
import DataTable from '../common/DataTable';
import Pagination from '../common/Pagination';
import dayjs from '../../lib/day';
import { ucwords } from '../../lib/strings';
import { useUser } from '../../contexts/UserContext';

import UserModal from './modals/UserModal';

const Users = () => {
  const { form, onChange } = useForm({ search: '' });
  const [editUser, setEditUser] = useState(null);
  const [filters, setFilters] = useState({
    page: 1,
    search: undefined,
    sortBy: 'userLastName',
    sortDir: 'ASC',
  });
  const queryClient = useQueryClient();
  const { hasPermission } = useUser();

  const { isLoading, data } = useFetchUsers(
    filters.page,
    filters.search,
    filters.sortBy,
    filters.sortDir,
  );

  const deleteUser = useDeleteUser(
    () => {
      toast.success('The admin has been successfully removed.');
      queryClient.invalidateQueries('admins');
    },
    { onError: (err) => { if (err.response?.status !== 403) toast.error(err.message); } },
  );

  const exportUsers = useExportUsers(
    (blob) => {
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      // Add timestamp to filename
      const timestamp = new Date().toISOString().split('T')[0];
      a.download = `users_export_${timestamp}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Users exported successfully!');
    },
    {
      onError: (err) => toast.error(err.message || 'Failed to export users'),
    },
  );

  const onSubmit = (e) => {
    e.preventDefault();
    setFilters({ ...filters, search: form.search });
  };

  const handleExport = () => {
    exportUsers.mutate();
  };

  // Permission-based check for export functionality
  const canExportUsers = hasPermission('users:export');

  return (
    <PrivateLayout>
      <Row>
        <Col>
          <Panel>
            <Row>
              <Col>
                <h2>Users</h2>
              </Col>
              <Col xs={6} sm={3} className="text-end">
                {/* Export button (users:export permission required) */}
                {canExportUsers && (
                  <Button
                    variant="outline-primary me-2"
                    onClick={handleExport}
                    disabled={exportUsers.isLoading}
                  >
                    <FontAwesomeIcon icon={faFileCsv} />
                    {exportUsers.isLoading ? ' Exporting...' : ' Export'}
                  </Button>
                )}
                {/* No Send Invite here (Nic 2026-09-22): a signup link only
                    goes out from a lead on the Onboarding Support board, so
                    nobody ends up with both a lead and a client account. */}
              </Col>
              <Col xs={12} sm={4} className="text-end">
                <form onSubmit={onSubmit}>
                  <Form.Control
                    name="search"
                    value={form.search}
                    type="search"
                    placeholder="Search..."
                    onChange={onChange}
                  ></Form.Control>
                </form>
              </Col>
            </Row>
            <br />
            <DataTable
              columns={[
                {
                  header: 'Last Name',
                  key: 'userLastName',
                  sortable: true,
                },
                {
                  header: 'First Name',
                  key: 'userFirstName',
                  sortable: true,
                },
                {
                  header: 'Preferred Name',
                  key: 'userPreferredName',
                  sortable: true,
                },
                {
                  header: 'Email',
                  key: 'userEmail',
                  sortable: true,
                },
                {
                  header: 'Date of Birth',
                  key: 'userDateOfBirth',
                  sortable: true,
                  render: (row) => {
                    return dayjs.utc(row.userDateOfBirth).format('MM/DD/YYYY');
                  },
                },
                {
                  header: 'Military Status',
                  key: 'userMilitaryStatus',
                  sortable: true,
                  render: (row) => ucwords(row.userMilitaryStatus),
                },
                {
                  header: '# Deps.',
                  key: 'userDependentsUnder18',
                  sortable: true,
                },
              ]}
              busy={isLoading}
              actions={hasPermission('clients:manage') ? [
                {
                  label: 'Edit User',
                  onClick: (row) => setEditUser(row),
                },
                {
                  divider: true,
                },
                {
                  label: 'Delete User',
                  onClick: (row) => {
                    if (
                      window.confirm(
                        'Are you sure you want to delete/deactivate this user?',
                      )
                    ) {
                      deleteUser.mutate(row.userId);
                    }
                  },
                },
              ] : undefined}
              data={data ? data.rows : []}
              defaultSort={`${filters.sortBy}:${filters.sortDir}`}
              onSortChange={(newSortBy, newSortDir) => {
                setFilters({
                  ...filters,
                  page: 1,
                  sortBy: newSortBy,
                  sortDir: newSortDir,
                });
              }}
            />

            {data && data.total > 0 ? (
              <Pagination
                page={filters.page}
                totalRows={data.total}
                rowsPerPage={50}
                onBack={() =>
                  setFilters({ ...filters, page: filters.page - 1 })
                }
                onForward={() =>
                  setFilters({ ...filters, page: filters.page + 1 })
                }
              />
            ) : null}
          </Panel>
        </Col>
      </Row>
      <UserModal
        show={editUser !== null}
        toggle={() => setEditUser(null)}
        user={editUser}
        onSuccess={() => queryClient.invalidateQueries('users')}
      />
    </PrivateLayout>
  );
};

export default Users;
